import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useMemo } from 'react'
import Loading from '~containers/general/loading'
import useSamsungSmartThingsOAuth2Request from '~repositories/hooks/samsung.smart-things/use-oauth2-request'
import useSocialLogIn from '~repositories/hooks/use-social-login'
import getOAuth2Params from '~utils/app/get-oauth2-params'
import removeOAuth2Params from '~utils/app/remove-oauth2-params'
import Mixpanel from '~utils/mixpanel'
import SamsungSmartThingsAuthConnected from '../auth/connected'

interface SamsungSmartThingsOAuth2RedirectProps {
  response: Record<string, string>
}
const SamsungSmartThingsOAuth2Redirected = ({
  response,
}: SamsungSmartThingsOAuth2RedirectProps) => {
  if (!response.code) {
    throw new Error('No code found in response')
  }

  const navigate = useNavigate()
  const {
    mutate: socialLogIn,
    isSuccess: isSocialLoginSuccess,
    error: socialLoginError,
  } = useSocialLogIn({
    onSuccess: () => {
      navigate({
        to: '/samsung/smart-things',
        resetScroll: true,
      })
    },
  })

  const {
    mutate: oauth2SignInWithSamsungAccount,
    isSuccess: isOAuth2Success,
    error: oauth2Error,
  } = useSamsungSmartThingsOAuth2Request()

  const isSuccess = useMemo(
    () => isSocialLoginSuccess || isOAuth2Success,
    [isSocialLoginSuccess, isOAuth2Success]
  )
  const error = useMemo(() => socialLoginError || oauth2Error, [socialLoginError, oauth2Error])

  const signIn = useCallback(async () => {
    const signInParams = getOAuth2Params()
    const anonymousCode = await Mixpanel.getDistinctId()

    if (!anonymousCode) {
      throw new Error('Anonymous code not found')
    }

    if (signInParams) {
      const mergedParams = {
        ...JSON.parse(signInParams),
        ...response,
      }

      removeOAuth2Params()

      oauth2SignInWithSamsungAccount({
        ...mergedParams,
        grant_type: 'authorization_code',
        code: mergedParams.code,
        client_id: mergedParams.client_id,
        state: mergedParams.state,
        redirect_uri: mergedParams.redirect_uri,
        callback_uri: mergedParams.callback_uri,
        anonymous_code: anonymousCode,
      })
    } else {
      socialLogIn({
        platform: 'samsung',
        access_token: response.code,
        anonymous_code: anonymousCode,
        is_web: true,
      })
    }
  }, [oauth2SignInWithSamsungAccount, response, socialLogIn])

  useEffect(() => {
    signIn()
  }, [signIn])

  /**
   * @TODO: 에러 핸들링 추가 (UI 수정 필요 + Failover 추가 필요)
   */
  if (error) {
    const axiosError = error as AxiosError
    const errorMessage = axiosError?.response?.data
      ? JSON.stringify(axiosError.response.data)
      : 'Unknown error'
    return (
      <main className="flex flex-col items-center justify-center h-screen p-5">
        <div className="flex flex-col items-center justify-center p-5 bg-extended-alabaster rounded-lg">
          <h1 className="text-2xl font-bold">Error</h1>
          <p className="text-lg text-gray-500">{errorMessage}</p>
        </div>
      </main>
    )
  }

  return !isSuccess ? (
    <Loading />
  ) : (
    <SamsungSmartThingsAuthConnected
      appName="SmartThings"
      appLink={import.meta.env.VITE_SAMSUNG_SMART_THINGS_APP_LINK}
    />
  )
}

export default SamsungSmartThingsOAuth2Redirected
